// خط الاستديو الحيّ عبر WebSocket.
//
// المستمع يرى مكالمته وحدها وموقعه في الطابور؛ المسؤول يرى الطابور
// كاملاً. التفريق يتم في الخادم لا هنا — إخفاء أسماء المنتظرين
// بالواجهة وحدها إخفاءٌ شكليّ.

import { useCallback, useEffect, useRef, useState } from 'react';
import { studioLineSocketUrl, isApiConfigured } from '../api/client';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;
const PING_INTERVAL_MS = 30000;

/** يطلب فتح مجلس الطيبين — حين تُحوَّل المكالمة إلى الهواء. */
export const OPEN_MAJLIS_EVENT = 'moslam:open-majlis';

const EMPTY = {
  isAdmin: false,
  adminOnline: false,
  queue: [],
  active: null,
  // مكالمات الزملاء المذيعين الجارية — للعرض: من مشغول بمن
  others: [],
  myCall: null,
  position: null,
  ahead: null,
  waitingTotal: 0,
};

/**
 * @param {boolean} enabled لا نفتح الاتصال إلا عند الحاجة فعلاً
 */
export function useStudioLine(enabled) {
  const [state, setState] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed | error
  const [errorMessage, setErrorMessage] = useState('');

  const socketRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const pingRef = useRef(null);
  const closedByUsRef = useRef(false);

  const send = useCallback((payload) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !isApiConfigured()) return;

    const url = studioLineSocketUrl();
    if (!url) return;

    setStatus('connecting');
    setErrorMessage('');

    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      setStatus('error');
      setErrorMessage('تعذر فتح الاتصال بالاستديو.');
      return;
    }

    socketRef.current = ws;

    ws.onopen = () => {
      attemptsRef.current = 0;
      setStatus('open');
      pingRef.current = setInterval(() => send({ type: 'ping' }), PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      switch (msg.type) {
        case 'state':
          setState({ ...EMPTY, ...msg });
          break;
        case 'error':
          setErrorMessage(msg.message || 'تعذر تنفيذ الطلب.');
          break;
        case 'transferred':
          // المكالمة صارت على الهواء: المجلس يُفتح عند الطرفين
          window.dispatchEvent(new Event(OPEN_MAJLIS_EVENT));
          break;
        case 'accepted':
        case 'ended':
          // الحالة الكاملة تصل في رسالة state التالية؛ هذه للتنبيه الفوري
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      clearInterval(pingRef.current);
      socketRef.current = null;
      if (closedByUsRef.current) return;

      setStatus('closed');
      // مصافحة WebSocket لا تكشف رمز حالة HTTP، فرفض الخادم يظهر كإغلاق
      const attempt = ++attemptsRef.current;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
      reconnectRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => setStatus('error');
  }, [enabled, send]);

  useEffect(() => {
    closedByUsRef.current = false;

    if (!enabled) {
      closedByUsRef.current = true;
      clearTimeout(reconnectRef.current);
      clearInterval(pingRef.current);
      socketRef.current?.close();
      socketRef.current = null;
      setState(EMPTY);
      setStatus('idle');
      return;
    }

    connect();

    return () => {
      closedByUsRef.current = true;
      clearTimeout(reconnectRef.current);
      clearInterval(pingRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, connect]);

  return {
    ...state,
    status,
    errorMessage,
    clearError: useCallback(() => setErrorMessage(''), []),
    requestCall: useCallback(
      ({ name, phone, topic }) => {
        const ok = send({ type: 'call', name, phone, topic });
        if (!ok) {
          setErrorMessage('تعذر إرسال طلب الاتصال. جارٍ إعادة الاتصال بالاستديو...');
          connect();
        }
        return ok;
      },
      [send, connect]
    ),
    cancelCall: useCallback(() => {
      const ok = send({ type: 'cancel' });
      if (!ok) connect();
      return ok;
    }, [send, connect]),
    acceptCall: useCallback(
      (callId) => {
        const ok = send({ type: 'accept', callId });
        if (!ok) {
          setErrorMessage('تعذر الرد على المكالمة. جارٍ إعادة الاتصال...');
          connect();
        }
        return ok;
      },
      [send, connect]
    ),
    declineCall: useCallback(
      (callId) => {
        const ok = send({ type: 'decline', callId });
        if (!ok) connect();
        return ok;
      },
      [send, connect]
    ),
    transferCall: useCallback(
      (callId) => {
        const ok = send({ type: 'transfer', callId });
        if (!ok) {
          setErrorMessage('تعذر التحويل. جارٍ إعادة الاتصال...');
          connect();
        }
        return ok;
      },
      [send, connect]
    ),
    endCall: useCallback(
      (callId) => {
        const ok = send({ type: 'end', callId });
        if (!ok) connect();
        return ok;
      },
      [send, connect]
    ),
  };
}
